import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import * as pdfjsLib from 'pdfjs-dist';
import JSZip from 'jszip';
import { supabase } from './supabase';
import { useSubscriptionStore } from '@/store/subscription';

// Set up PDF.js worker — use unpkg which serves directly from npm (CDNJS lags behind and may 404)
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

export interface DocumentAnalysisResult {
  summary: string;
  keyPoints: string[];
  risks: string[];
  recommendations: string[];
  confidence: number;
}

export interface ExtractedMilestone {
  name: string;
  startDate: string | null;
  dueDate: string | null;
  status: 'not_started' | 'in_progress' | 'completed' | 'overdue';
  owner: string | null;
}

export interface ExtractedRisk {
  summary: string;
  description: string | null;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'mitigating' | 'mitigated' | 'closed';
  dateIdentified: string | null;
  owner: string | null;
  mitigation: string | null;
}

export interface ExtractedKpiHistoryPoint {
  date: string; // YYYY-MM-DD
  value: number;
}

export interface ExtractedKPI {
  name: string;
  target: number | null;
  current: number | null;
  unit: string | null;
  /** Historical data points for sparklines / trend deltas, oldest → newest. */
  history: ExtractedKpiHistoryPoint[];
}

export interface ProjectExtractionResult {
  projectName: string;
  trackerId: string | null; // Internal project ID/tracker code
  description: string;
  overallHealth: 'green' | 'yellow' | 'red';
  startDate: string | null;
  endDate: string | null;
  completionPercentage: number | null;
  milestones: ExtractedMilestone[];
  risks: ExtractedRisk[];
  kpis: ExtractedKPI[];
  accomplishments: string[];
  priorities: string[];
  summary: string;
  confidence: number;
  extractedNames: string[]; // Names extracted from documents for owner autocomplete
}

/**
 * Check if user has access to AI features
 * Throws an error if user is not on Pro tier
 */
function checkAIAccess(): void {
  const canUseAI = useSubscriptionStore.getState().canUseAI();
  if (!canUseAI) {
    throw new Error('AI features require a Pro subscription. Please upgrade to continue.');
  }
}

/**
 * Analyzes one or more documents and generates a project summary
 * @param documents Array of document texts to analyze
 * @returns Analysis result with summary, key points, risks, and recommendations
 */
export async function analyzeDocuments(documents: string[], workspaceId?: string): Promise<DocumentAnalysisResult> {
  // Check subscription before making AI call
  checkAIAccess();

  if (!documents || documents.length === 0) {
    throw new Error('No documents provided for analysis');
  }

  try {
    console.log('Calling Supabase Edge Function to analyze documents');

    // Get the current session to include auth token
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase.functions.invoke('analyze-documents', {
      body: { documents, workspace_id: workspaceId },
    });

    if (error) {
      console.error('Edge Function error:', error);
      throw error;
    }

    console.log('Edge Function response:', data);

    return {
      summary: data.summary || '',
      keyPoints: data.keyPoints || [],
      risks: data.risks || [],
      recommendations: data.recommendations || [],
      confidence: data.confidence || 0.85
    };
  } catch (error) {
    console.error('Error analyzing documents:', error);
    throw new Error(`Failed to analyze documents: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Extracts structured project data from documents using AI
 * @param documents Array of document texts to analyze
 * @returns Extracted project data including name, description, health, milestones, risks, etc.
 */
export async function extractProjectData(documents: string[], workspaceId?: string): Promise<ProjectExtractionResult> {
  // Check subscription before making AI call
  checkAIAccess();

  if (!documents || documents.length === 0) {
    throw new Error('No documents provided for extraction');
  }

  try {
    console.log('Calling Supabase Edge Function to extract project data');

    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase.functions.invoke('extract-project-data', {
      body: { documents, workspace_id: workspaceId },
    });

    if (error) {
      console.error('Edge Function error:', error);
      throw error;
    }

    console.log('Project extraction response:', data);

    return {
      projectName: data.projectName || 'Untitled Project',
      trackerId: data.trackerId || null,
      description: data.description || '',
      overallHealth: data.overallHealth || 'yellow',
      startDate: data.startDate || null,
      endDate: data.endDate || null,
      completionPercentage: data.completionPercentage ?? null,
      milestones: data.milestones || [],
      risks: data.risks || [],
      kpis: data.kpis || [],
      accomplishments: data.accomplishments || [],
      priorities: data.priorities || [],
      summary: data.summary || '',
      confidence: data.confidence || 0.85,
      extractedNames: data.extractedNames || [],
    };
  } catch (error) {
    console.error('Error extracting project data:', error);
    throw new Error(`Failed to extract project data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

export interface GeneratedTask {
  title: string;
  description: string | null;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate: string | null;
  assignedTo: string | null;
}

/**
 * Generates a task list from documents using AI
 * @param documents Array of document texts
 * @param startDate Optional project start date (YYYY-MM-DD)
 * @param endDate Optional project end date (YYYY-MM-DD)
 * @param projectContext Optional project name/description for context
 */
export async function generateTasksFromDocuments(
  documents: string[],
  startDate?: string | null,
  endDate?: string | null,
  projectContext?: string
): Promise<GeneratedTask[]> {
  checkAIAccess();

  if (!documents || documents.length === 0) {
    throw new Error('No documents provided for task generation');
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const { data, error } = await supabase.functions.invoke('generate-tasks', {
      body: { documents, startDate, endDate, projectContext },
    });

    if (error) {
      console.error('Edge Function error:', error);
      throw error;
    }

    return data.tasks || [];
  } catch (error) {
    console.error('Error generating tasks:', error);
    throw new Error(`Failed to generate tasks: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Regenerates the executive summary for an existing project. The edge function
 * pulls the project's current state (meta + milestones + risks + KPIs +
 * priorities + accomplishments) via RLS and asks the LLM to write a fresh
 * status paragraph. Returns the summary text — caller is responsible for
 * persisting it onto the project row.
 */
export async function generateExecutiveSummary(projectId: string): Promise<string> {
  checkAIAccess();

  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Not authenticated');
  }

  const { data, error } = await supabase.functions.invoke('generate-executive-summary', {
    body: { project_id: projectId },
  });

  if (error) {
    console.error('generate-executive-summary error:', error);
    throw error;
  }

  return (data?.summary as string) ?? '';
}

/**
 * Extracts text from PDF file
 */
async function extractTextFromPDF(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const textParts: string[] = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const pageText = textContent.items
      .map((item: any) => item.str)
      .join(' ');
    textParts.push(pageText);
  }

  return textParts.join('\n\n');
}

/**
 * Extracts text from DOCX file
 */
async function extractTextFromDOCX(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value;
}

/**
 * Extracts text from XLSX file
 */
async function extractTextFromXLSX(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const textParts: string[] = [];

  workbook.SheetNames.forEach((sheetName) => {
    const worksheet = workbook.Sheets[sheetName];
    const sheetText = XLSX.utils.sheet_to_txt(worksheet);
    textParts.push(`Sheet: ${sheetName}\n${sheetText}`);
  });

  return textParts.join('\n\n');
}

/**
 * Extracts text from a PPTX file. PPTX is a zip of OOXML; slides live at
 * `ppt/slides/slideN.xml` and speaker notes (when present) at
 * `ppt/notesSlides/notesSlideN.xml`. Inside each, every visible text run
 * is wrapped in an `<a:t>...</a:t>` element. We walk the zip, pull those
 * runs out, and join them with sensible separators.
 *
 * Legacy `.ppt` (binary CFB) is intentionally NOT supported — it's a
 * different file format and there's no maintained browser-side parser
 * worth adding. Users with .ppt files should re-save as .pptx.
 */
async function extractTextFromPPTX(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(arrayBuffer);

  const slidePathsByNumber = (prefix: string, suffix: string) =>
    Object.keys(zip.files)
      .filter(name => {
        const re = new RegExp(`^${prefix}(\\d+)${suffix}$`);
        return re.test(name);
      })
      .sort((a, b) => {
        // Numeric sort: slide10 must come after slide9, not before.
        const numOf = (s: string) => {
          const m = s.match(/(\d+)\.xml$/);
          return m ? parseInt(m[1], 10) : 0;
        };
        return numOf(a) - numOf(b);
      });

  const slidePaths = slidePathsByNumber('ppt/slides/slide', '\\.xml');
  const notesPaths = slidePathsByNumber('ppt/notesSlides/notesSlide', '\\.xml');

  const decodeEntities = (s: string) =>
    s.replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'");

  const extractRuns = (xml: string): string => {
    // <a:t> wraps every visible text run. Attributes like xml:space="preserve"
    // are common, hence the [^>]* on the opening tag. The body is plain text
    // (no nested elements), so a single capture is enough.
    const matches = xml.match(/<a:t[^>]*>([^<]*)<\/a:t>/g) ?? [];
    return matches
      .map(m => m.replace(/<a:t[^>]*>/, '').replace(/<\/a:t>$/, ''))
      .map(decodeEntities)
      .filter(t => t.length > 0)
      .join(' ');
  };

  const sections: string[] = [];
  for (let i = 0; i < slidePaths.length; i++) {
    const xml = await zip.files[slidePaths[i]].async('string');
    const slideText = extractRuns(xml).trim();
    const slideNum = i + 1;

    let notesText = '';
    // Notes file numbers usually align with slide numbers, but treat as
    // best-effort: if a notesSlideN.xml exists for this slide number, append.
    const matchingNotes = notesPaths.find(p => {
      const m = p.match(/(\d+)\.xml$/);
      return m && parseInt(m[1], 10) === slideNum;
    });
    if (matchingNotes) {
      const notesXml = await zip.files[matchingNotes].async('string');
      notesText = extractRuns(notesXml).trim();
    }

    if (slideText || notesText) {
      const parts = [`Slide ${slideNum}: ${slideText}`];
      if (notesText) parts.push(`Notes: ${notesText}`);
      sections.push(parts.join('\n'));
    }
  }

  return sections.join('\n\n');
}

/**
 * Extracts text from plain text file
 */
async function extractTextFromPlainText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const text = e.target?.result as string;
      resolve(text);
    };

    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${file.name}`));
    };

    reader.readAsText(file);
  });
}

/**
 * Extracts text content from a File object (supports PDF, DOCX, XLSX, TXT, MD, CSV)
 * @param file File to extract text from
 * @returns Extracted text content
 */
export async function extractTextFromFile(file: File): Promise<string> {
  const extension = file.name.split('.').pop()?.toLowerCase();

  try {
    switch (extension) {
      case 'pdf':
        return await extractTextFromPDF(file);
      case 'docx':
        return await extractTextFromDOCX(file);
      case 'xlsx':
      case 'xls':
        return await extractTextFromXLSX(file);
      case 'pptx':
        return await extractTextFromPPTX(file);
      case 'txt':
      case 'md':
      case 'csv':
        return await extractTextFromPlainText(file);
      default:
        throw new Error(`Unsupported file type: ${extension}`);
    }
  } catch (error) {
    console.error(`Error extracting text from ${file.name}:`, error);
    throw new Error(`Failed to extract text from ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}
